import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SocialLinks from "./SocialLinks";

// Top items shown directly in footer (rest reachable via "Ver todas").
const TOP_CITIES = [
    { slug: "santiago", name: "Santiago" },
    { slug: "vina-del-mar", name: "Viña del Mar" },
    { slug: "valparaiso", name: "Valparaíso" },
    { slug: "concepcion", name: "Concepción" },
    { slug: "antofagasta", name: "Antofagasta" },
    { slug: "temuco", name: "Temuco" },
    { slug: "la-serena", name: "La Serena" },
    { slug: "puerto-montt", name: "Puerto Montt" },
];

const TOP_REGIONS = [
    { slug: "metropolitana", name: "Metropolitana" },
    { slug: "valparaiso", name: "Valparaíso" },
    { slug: "biobio", name: "Biobío" },
    { slug: "araucania", name: "Araucanía" },
    { slug: "antofagasta", name: "Antofagasta" },
    { slug: "coquimbo", name: "Coquimbo" },
];

const MODALITIES = [
    { slug: "residencial", name: "Residencial" },
    { slug: "ambulatoria", name: "Ambulatoria" },
    { slug: "online", name: "Online" },
    { slug: "mujeres", name: "Para mujeres" },
    { slug: "adolescentes", name: "Para adolescentes" },
];

const TOP_COMUNAS = [
    { slug: "providencia", name: "Providencia" },
    { slug: "las-condes", name: "Las Condes" },
    { slug: "nunoa", name: "Ñuñoa" },
    { slug: "maipu", name: "Maipú" },
    { slug: "puente-alto", name: "Puente Alto" },
];

function Column({ title, children, testId }) {
    return (
        <div data-testid={testId}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                {title}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">{children}</ul>
        </div>
    );
}

function FootLink({ to, href, children, testId }) {
    const className =
        "group inline-flex items-center gap-1 text-slate-400 transition-colors hover:text-cyan-400";
    if (href) {
        return (
            <li>
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                    data-testid={testId}
                >
                    {children}
                </a>
            </li>
        );
    }
    return (
        <li>
            <Link to={to} className={className} data-testid={testId}>
                {children}
            </Link>
        </li>
    );
}

function ViewAll({ to, label, testId }) {
    return (
        <li className="pt-1">
            <Link
                to={to}
                data-testid={testId}
                className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-cyan-400 hover:text-cyan-300"
            >
                {label}
                <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
        </li>
    );
}

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="border-t border-slate-800 bg-slate-950"
        >
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                <div className="grid gap-10 lg:grid-cols-12">
                    {/* Brand */}
                    <div className="lg:col-span-4">
                        <Link to="/" className="flex items-center gap-3">
                            <img
                                src="https://sinadicciones.org/favicon-192x192.png"
                                alt="Logo"
                                className="h-10 w-10 rounded-md object-contain"
                            />
                            <div className="leading-tight">
                                <div className="text-sm font-bold text-slate-50">
                                    Centro de Rehabilitación
                                </div>
                                <div className="text-xs text-cyan-400">
                                    de Drogas · Chile
                                </div>
                            </div>
                        </Link>
                        <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
                            Directorio independiente de centros de rehabilitación de
                            drogas verificados en Chile. Información clínica curada,
                            cobertura nacional y cero sesgo comercial. En colaboración
                            con la red SinAdicciones.org.
                        </p>
                        <div className="mt-6">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                                Síguenos
                            </p>
                            <div className="mt-3">
                                <SocialLinks variant="footer" />
                            </div>
                        </div>
                        <div className="mt-6 inline-flex items-center gap-2 rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-300">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-400" />
                            58 URLs · 88+ centros · 16 regiones
                        </div>
                    </div>

                    {/* Ciudades */}
                    <div className="lg:col-span-2">
                        <Column title="Ciudades" testId="footer-col-ciudades">
                            {TOP_CITIES.map((c) => (
                                <FootLink
                                    key={c.slug}
                                    to={`/ciudades/${c.slug}`}
                                    testId={`footer-city-${c.slug}`}
                                >
                                    {c.name}
                                </FootLink>
                            ))}
                            <ViewAll
                                to="/ciudades"
                                label="Ver 17 ciudades"
                                testId="footer-cities-all"
                            />
                        </Column>
                    </div>

                    {/* Regiones + Comunas Santiago */}
                    <div className="lg:col-span-2">
                        <Column title="Regiones" testId="footer-col-regiones">
                            {TOP_REGIONS.map((r) => (
                                <FootLink
                                    key={r.slug}
                                    to={`/regiones/${r.slug}`}
                                    testId={`footer-region-${r.slug}`}
                                >
                                    {r.name}
                                </FootLink>
                            ))}
                            <ViewAll
                                to="/regiones"
                                label="Ver 16 regiones"
                                testId="footer-regions-all"
                            />
                        </Column>

                        <div className="mt-8">
                            <Column title="Santiago" testId="footer-col-comunas">
                                {TOP_COMUNAS.map((c) => (
                                    <FootLink
                                        key={c.slug}
                                        to={`/santiago/${c.slug}`}
                                        testId={`footer-comuna-${c.slug}`}
                                    >
                                        {c.name}
                                    </FootLink>
                                ))}
                                <ViewAll
                                    to="/santiago"
                                    label="Ver 10 comunas"
                                    testId="footer-comunas-all"
                                />
                            </Column>
                        </div>
                    </div>

                    {/* Modalidades */}
                    <div className="lg:col-span-2">
                        <Column title="Modalidades" testId="footer-col-modalidades">
                            {MODALITIES.map((m) => (
                                <FootLink
                                    key={m.slug}
                                    to={`/modalidades/${m.slug}`}
                                    testId={`footer-modality-${m.slug}`}
                                >
                                    {m.name}
                                </FootLink>
                            ))}
                            <ViewAll
                                to="/modalidades"
                                label="Ver modalidades"
                                testId="footer-modalities-all"
                            />
                        </Column>

                        <div className="mt-8">
                            <Column title="Recursos" testId="footer-col-recursos">
                                <FootLink to="/centros">Directorio de centros</FootLink>
                                <FootLink to="/blog">Blog</FootLink>
                                <FootLink to="/nosotros">Quiénes somos</FootLink>
                            </Column>
                        </div>
                    </div>

                    {/* Red SinAdicciones */}
                    <div className="lg:col-span-2">
                        <Column title="Red SinAdicciones" testId="footer-col-sinadicciones">
                            <FootLink href="https://sinadicciones.org">
                                Plataforma principal
                            </FootLink>
                            <FootLink href="https://sinadicciones.org/centros">
                                Centros oficiales
                            </FootLink>
                            <FootLink href="https://sinadicciones.org/agregar-centro">
                                Agregar mi centro
                            </FootLink>
                            <FootLink href="https://sinadicciones.org/asesoria">
                                Asesoría experta
                            </FootLink>
                        </Column>

                        <div className="mt-8">
                            <Column title="Legal" testId="footer-col-legal">
                                <FootLink href="/sitemap.xml">Sitemap</FootLink>
                                <FootLink href="/robots.txt">Robots.txt</FootLink>
                            </Column>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 flex flex-col gap-3 border-t border-slate-800 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
                    <p>
                        © {new Date().getFullYear()} centroderehabilitaciondedrogas.cl
                        — Todos los derechos reservados.
                    </p>
                    <p className="sm:text-right">
                        Sitio informativo. No reemplaza atención médica. En
                        emergencias, llama a SAMU{" "}
                        <a
                            href="tel:131"
                            className="font-semibold text-cyan-400 hover:text-cyan-300"
                        >
                            131
                        </a>
                        .
                    </p>
                </div>
            </div>
        </footer>
    );
}
